import type { ReactNode } from "react"
import { AlertTriangle, Camera, Check, CheckCircle2, ChevronRight, CircleDashed, CloudOff, FileText, Info, Mail, ScanBarcode } from "lucide-react"
import { cn } from "@/lib/utils"
import { barcodeToSave, billTermsText, ptDate, type ConfirmStepInfo, type ReceiptDetail, type ReviewIssue } from "./api"

/**
 * Coluna da direita do detalhe (Fase 2d): o que o Koortex verificou, o que
 * falta resolver (tocar leva à linha), o que vai ser escrito no Odoo, a origem
 * e o «Conferir caixas». Só mostra — não decide nada que o servidor não diga.
 */

/** Título de cartão em versaletes. */
export function CardTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <h2 className="flex-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</h2>
      {right}
    </div>
  )
}

export function Panel({ children, className, id }: { children: ReactNode; className?: string; id?: string }) {
  return (
    <section id={id} className={cn("rounded-xl border bg-card p-4", className)}>
      {children}
    </section>
  )
}

type CheckState = "ok" | "warn" | "info"

export interface ValidationCheck {
  key: string
  label: string
  state: CheckState
  detail?: string
}

/** As verificações do cartão «Validação Koortex», a partir dos avisos da leitura e dos problemas. */
export function validationChecks(detail: ReceiptDetail): ValidationCheck[] {
  const w = detail.receipt.warnings_json ?? []
  const has = (prefix: string) => w.some((x) => x === prefix || x.startsWith(`${prefix}:`))
  const issueKinds = new Set(detail.issues.map((i) => i.kind))
  const qrMismatch = w.filter((x) => x.startsWith("qr_mismatch:")).map((x) => x.split(":")[1])
  // Só os artigos com número de série no Odoo têm séries a conferir (0.38.0): as
  // lidas num artigo sem rastreio seguem o aviso «ativar ou seguir sem séries».
  const tracked = detail.lines.filter((l) => l.odoo_tracking === "serial")
  const untrackedRead = detail.lines.some((l) => l.odoo_tracking !== "serial" && l.serials.length > 0)
  return [
    qrMismatch.length > 0
      ? { key: "qr", label: "QR fiscal / ATCUD", state: "warn", detail: `Não bate com o documento: ${qrMismatch.join(", ")}` }
      : has("qr_missing")
        ? { key: "qr", label: "QR fiscal / ATCUD", state: "info", detail: "Sem QR fiscal lido (normal numa fatura estrangeira)" }
        : { key: "qr", label: "QR fiscal / ATCUD", state: "ok", detail: detail.receipt.atcud ? `ATCUD ${detail.receipt.atcud}` : undefined },
    { key: "soma", label: "Soma das linhas", state: has("line_totals_mismatch") ? "warn" : "ok", detail: has("line_totals_mismatch") ? "As linhas não somam o total" : undefined },
    { key: "iva", label: "IVA por taxa", state: has("tax_totals_mismatch") ? "warn" : "ok", detail: has("tax_totals_mismatch") ? "O IVA não bate com o total" : undefined },
    {
      key: "series",
      label: "Séries = quantidade",
      state: tracked.length === 0 ? "info" : issueKinds.has("serial_count") ? "warn" : "ok",
      detail:
        tracked.length === 0
          ? untrackedRead
            ? "Sem artigos com número de série no Odoo — as séries lidas não se contam"
            : "Sem artigos com série"
          : undefined,
    },
    issueKinds.has("supplier_mismatch")
      ? { key: "encomenda", label: "Encomenda do mesmo fornecedor", state: "warn", detail: "A encomenda é de outro fornecedor" }
      : issueKinds.has("no_order")
        ? { key: "encomenda", label: "Encomenda do mesmo fornecedor", state: "warn", detail: "Sem encomenda escolhida" }
        : { key: "encomenda", label: "Encomenda do mesmo fornecedor", state: "ok", detail: detail.receipt.order_names.join(", ") || undefined },
  ]
}

function CheckIcon({ state }: { state: CheckState }) {
  if (state === "ok") return <Check className="h-4 w-4 shrink-0 text-emerald-600" />
  if (state === "warn") return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
  return <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
}

export function ValidationCard({ detail, compact = false }: { detail: ReceiptDetail; compact?: boolean }) {
  const checks = validationChecks(detail)
  const reconciled = detail.issues.length === 0 && checks.every((c) => c.state !== "warn")
  const confidence = detail.receipt.confidence === null || detail.receipt.confidence === undefined ? null : Math.round(Number(detail.receipt.confidence) * 100)
  const fallback = (detail.receipt.warnings_json ?? []).includes("serials_fallback_reader")
  return (
    <Panel>
      <CardTitle right={confidence !== null && <span className="text-sm tabular-nums text-muted-foreground">confiança {confidence}%</span>}>Validação Koortex</CardTitle>
      <p className={cn("mb-3 flex items-center gap-2 text-sm font-semibold", reconciled ? "text-emerald-700" : "text-amber-700")}>
        {reconciled ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
        {reconciled ? "Dados reconciliados" : "A rever"}
      </p>
      <ul className={cn("grid gap-2 text-sm", compact && "sm:grid-cols-2")}>
        {checks.map((c) => (
          <li key={c.key} className="flex items-start gap-2">
            <span className="mt-0.5">
              <CheckIcon state={c.state} />
            </span>
            <span className="min-w-0">
              <span className="font-medium">{c.label}</span>
              {c.detail && <span className="block text-xs text-muted-foreground">{c.detail}</span>}
            </span>
          </li>
        ))}
        {fallback && (
          <li className="flex items-start gap-2">
            <span className="mt-0.5">
              <Info className="h-4 w-4 text-sky-700" />
            </span>
            <span>
              <span className="font-medium">Leitor de recurso</span>
              <span className="block text-xs text-muted-foreground">As séries foram lidas por um segundo leitor — confira-as.</span>
            </span>
          </li>
        )}
      </ul>
    </Panel>
  )
}

export function BeforeConfirmCard({
  issues,
  editable,
  onJump,
  actions,
}: {
  issues: ReviewIssue[]
  editable: boolean
  onJump: (issue: ReviewIssue) => void
  /** Botões de um aviso (confirmar fornecedor, «Conferi no documento»). */
  actions: (issue: ReviewIssue) => ReactNode
}) {
  if (!editable) return null
  return (
    <Panel id="antes-de-confirmar">
      <CardTitle right={<span className="text-sm tabular-nums text-muted-foreground">{issues.length}</span>}>Antes de confirmar</CardTitle>
      {issues.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4" /> Nada por resolver.
        </p>
      ) : (
        <ul className="space-y-2">
          {issues.map((issue, i) => (
            <li key={`${issue.kind}-${issue.line_index}-${i}`} className="rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
              <button type="button" onClick={() => onJump(issue)} className="flex min-h-11 w-full items-start gap-2 p-2.5 text-left hover:bg-amber-100/60">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  {issue.line_index !== null && <span className="font-semibold">Linha {issue.line_index + 1}: </span>}
                  {issue.message}
                </span>
                <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 opacity-60" />
              </button>
              {actions(issue) && <div className="flex flex-wrap gap-2 px-2.5 pb-2.5">{actions(issue)}</div>}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

/** Os passos da confirmação, com o que cada um escreve; depois de confirmar, o progresso. */
export function WillWriteCard({ detail, steps, running }: { detail: ReceiptDetail; steps: ConfirmStepInfo[]; running: boolean }) {
  const { receipt, lines } = detail
  const orderName = (id: string) => {
    const i = receipt.order_ids.indexOf(Number(id))
    return i >= 0 ? receipt.order_names[i] ?? id : id
  }
  const describe = (key: string, label: string): { title: string; hint?: string } => {
    if (key === "verificar") return { title: "Verificar no Odoo", hint: "Quantidades, encomendas e séries repetidas — não escreve." }
    if (key === "fatura") {
      return { title: "Fatura de fornecedor em rascunho", hint: [`${receipt.document_number ?? "Sem número"}`, billTermsHint(detail), "a contabilidade publica-a"].filter(Boolean).join(" · ") }
    }
    if (key === "referencia") return { title: "Referência do fornecedor nas encomendas", hint: partnerRefHint(detail) }
    if (key.startsWith("rececao:")) {
      const orderId = key.split(":")[1]!
      const n = lines.filter((l) => String(l.purchase_order_id) === orderId).reduce((s, l) => s + l.serials.length, 0)
      return { title: `Séries na receção de ${orderName(orderId)}: ${n} série${n === 1 ? "" : "s"}`, hint: "Uma linha por série na receção do Odoo." }
    }
    if (key.startsWith("stock:")) {
      const orderId = key.split(":")[1]!
      const done = receipt.write_log_json.stock?.[orderId]
      const preview = detail.stock_preview?.find((p) => String(p.order_id) === orderId)
      return { title: "Dar entrada no stock", hint: stockHint(orderName(orderId), done, preview) }
    }
    if (key === "codigos") {
      const n = lines.filter((l) => l.match_method === "manual" && l.supplier_code).length
      return { title: "Códigos de fornecedor", hint: n > 0 ? `${n} código(s) novo(s) aprendido(s)` : "Nada de novo a aprender" }
    }
    if (key === "codigos_barras") {
      const toSave = lines
        .map((l) => ({ l, code: barcodeToSave(l) }))
        .filter((x): x is { l: (typeof lines)[number]; code: string } => x.code !== null)
      return {
        title: "Códigos de barras dos artigos",
        hint: toSave.map(({ l, code }) => `EAN ${code} → ${l.odoo_default_code ?? l.odoo_product_name ?? `linha ${l.line_index + 1}`}`).join(" · ") || "Os EAN do documento",
      }
    }
    if (key === "series") return { title: "Tabela de séries da app", hint: `${lines.reduce((s, l) => s + l.serials.length, 0)} série(s)` }
    if (key === "recebido") {
      const n = lines.filter((l) => l.line_kind === "diversos" && l.purchase_line_id).length
      return { title: "Recebido das linhas Diversos", hint: `${n} linha(s) Diversos — sem stock, o «Recebido» na encomenda.` }
    }
    return { title: label }
  }
  if (steps.length === 0) return null
  const started = steps.some((s) => s.done)
  return (
    <Panel>
      <CardTitle>O que vai ser escrito no Odoo</CardTitle>
      <ol className="space-y-2.5 text-sm">
        {steps.map((s, i) => {
          const d = describe(s.key, s.label)
          const current = running && !s.done && steps.slice(0, i).every((x) => x.done)
          return (
            <li key={s.key} className="flex items-start gap-2.5">
              <span className="mt-0.5">
                {s.done ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : current ? (
                  <CircleDashed className="h-4 w-4 animate-spin text-sky-600" />
                ) : (
                  <span className="block h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
                )}
              </span>
              <span className="min-w-0">
                <span className={cn("font-medium", started && !s.done && !current && "text-muted-foreground")}>{d.title}</span>
                {d.hint && <span className="block text-xs text-muted-foreground">{d.hint}</span>}
              </span>
            </li>
          )
        })}
      </ol>
    </Panel>
  )
}

/** Vencimento e condições na fatura: depois de escrita, como ficou; antes, o que vai. */
function billTermsHint(detail: ReceiptDetail): string | null {
  const written = billTermsText(detail.receipt.write_log_json.bill)
  if (written) return written
  const t = detail.payment_terms
  if (!t) return null
  const due = ptDate(t.due_date)
  if (t.days !== null && !t.mismatch) return `condição a ${t.days} dias, se houver igual no Odoo${due ? ` (vence a ${due})` : ""}`
  return due ? `vence a ${due}` : null
}

/** «P02810: 1/4518 _ STOCK → 1/4518 _ STOCK · 1/2944»; depois de escrito, o que ficou (ou porque não). */
function partnerRefHint(detail: ReceiptDetail): string {
  const { receipt } = detail
  const number = receipt.document_number ?? ""
  const done = receipt.write_log_json.partner_refs
  if (done && Object.keys(done).length > 0) {
    return Object.values(done)
      .map((e) =>
        e.status === "escrita"
          ? `${e.order_name ?? "Encomenda"}: «${e.after ?? number}»`
          : e.status === "ja_tinha"
            ? `${e.order_name ?? "Encomenda"}: já tinha ${number}`
            : (e.message ?? `${e.order_name ?? "Encomenda"}: não escrita`),
      )
      .join(" · ")
  }
  const preview = detail.partner_ref_preview ?? []
  if (preview.length === 0) return `${number} na referência do fornecedor de ${receipt.order_names.join(", ") || "cada encomenda"}`
  return preview
    .map((p) =>
      !p.changes
        ? `${p.order_name ?? p.order_id}: já tem ${number}`
        : p.before
          ? `${p.order_name ?? p.order_id}: «${p.before}» → «${p.after}»`
          : `${p.order_name ?? p.order_id}: «${p.after}»`,
    )
    .join(" · ")
}

const plural = (n: number, one: string, many: string) => `${String(n).replace(".", ",")} ${n === 1 ? one : many}`

/**
 * «WH/IN/00123: 5 artigos; parcial → entrega pendente com o resto». Depois de
 * escrito, o que aconteceu (validada, entrega pendente, ou porque não).
 */
function stockHint(
  order: string,
  done: NonNullable<ReceiptDetail["receipt"]["write_log_json"]["stock"]>[string] | undefined,
  preview: NonNullable<ReceiptDetail["stock_preview"]>[number] | undefined,
): string {
  if (done && done.status !== "pendente") {
    const where = done.picking_name ?? order
    if (done.status === "nada") return `${order}: nada a dar entrada (já recebido no Odoo).`
    if (done.status === "nao_validada") return `${where}: não validada — ${done.message ?? "valide no Odoo"}`
    return `${where} validada${done.backorder ? ` · entrega pendente ${done.backorder.name ?? ""} com o resto` : ""}.`
  }
  if (!preview) return `Validar a receção de ${order} no Odoo com as quantidades do documento.`
  if (preview.units === 0) return `${order}: nada a dar entrada (já recebido no Odoo).`
  const where = preview.picking_name ?? `receção de ${order}`
  return `${where}: ${plural(preview.articles, "artigo", "artigos")}, ${plural(preview.units, "unidade", "unidades")}${preview.partial ? "; parcial → entrega pendente com o resto" : ""}.`
}

export function OriginCard({ detail }: { detail: ReceiptDetail }) {
  const r = detail.receipt
  const Icon = r.origin === "email" ? Mail : r.origin === "foto" ? Camera : FileText
  const label = r.origin === "email" ? `Email${r.origin_detail ? ` de ${r.origin_detail}` : ""}` : r.origin === "foto" ? "Foto" : "Ficheiro"
  return (
    <Panel>
      <CardTitle>Origem</CardTitle>
      <p className="flex items-start gap-2 text-sm">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 break-words">
          {label}
          {r.source_case_number && <span className="block text-xs text-muted-foreground">Caso {r.source_case_number}</span>}
          <span className="block text-xs text-muted-foreground">Carregado em {new Date(r.created_at).toLocaleString("pt-PT")}</span>
        </span>
      </p>
    </Panel>
  )
}

export function BoxCheckCard({
  done,
  total,
  pending,
  corrections,
  editable,
  onOpen,
}: {
  done: number
  total: number
  pending: number
  corrections: Array<{ from: string; to: string }>
  editable: boolean
  onOpen: () => void
}) {
  if (total === 0) return null
  return (
    <Panel>
      <CardTitle right={<span className="text-sm font-semibold tabular-nums">{done} / {total}</span>}>Conferir caixas</CardTitle>
      <p className="mb-3 text-sm text-muted-foreground">
        {done} / {total} caixas conferidas. Opcional — ler as caixas apanha uma série mal lida da fatura.
      </p>
      {corrections.map((c, i) => (
        <p key={i} className="mb-1 text-xs text-sky-800">
          Trocada {c.from} → {c.to} ao conferir.
        </p>
      ))}
      {pending > 0 && (
        <p className="mb-2 flex items-center gap-1.5 text-sm text-amber-700">
          <CloudOff className="h-4 w-4" /> {pending} por gravar — guardadas no telemóvel.
        </p>
      )}
      {editable && (
        <button type="button" onClick={onOpen} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border text-sm font-medium hover:bg-muted sm:h-9">
          <ScanBarcode className="h-4 w-4" /> Conferir caixas
        </button>
      )}
    </Panel>
  )
}
